import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import Navbar from "@/components/Navbar";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import InstallPrompt from "@/components/InstallPrompt";
import ProtectedRoute from "@/components/ProtectedRoute";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import CreateJob from "@/pages/CreateJob";
import JobDetail from "@/pages/JobDetail";
import CandidateStart from "@/pages/CandidateStart";
import InterviewRoom from "@/pages/InterviewRoom";
import InterviewComplete from "@/pages/InterviewComplete";
import Review from "@/pages/Review";
import Billing from "@/pages/Billing";
import BillingSuccess from "@/pages/BillingSuccess";
import AdminDashboard from "@/pages/AdminDashboard";
import Settings from "@/pages/Settings";import Learn from "@/pages/Learn";
import OnboardingTour from "@/components/OnboardingTour";
import BriefShare from "@/pages/BriefShare";
import Team from "@/pages/Team";
import Applicants from "@/pages/Applicants";
import Compare from "@/pages/Compare";
import AcceptInvite from "@/pages/AcceptInvite";
import PublicCareers from "@/pages/PublicCareers";
import PublicApply from "@/pages/PublicApply";
import ApplicationStatus from "@/pages/ApplicationStatus";
import UploadResume from "@/pages/UploadResume";
import DigestAction from "@/pages/DigestAction";
import WithdrawApplication from "@/pages/WithdrawApplication";
import BgCheckDataForm from "@/pages/BgCheckDataForm";
import PolicyPage from "@/pages/PolicyPage";
import LiveHost from "@/pages/LiveHost";
import LiveCandidate from "@/pages/LiveCandidate";

function Shell({ children, hideNav = false }) {
  return (
    <div className="App min-h-screen bg-white text-slate-900">
      <ImpersonationBanner />
      {!hideNav && <Navbar />}
      {!hideNav && <OnboardingTour />}
      {children}
    </div>
  );
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user ? <Navigate to="/dashboard" replace /> : <Landing />;
}

function App() {
  useEffect(() => {
    document.title = "Hireloop PRO — AI Video Interview Platform";
  }, []);

  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route
            path="/"
            element={
              <Shell>
                <RootRedirect />
              </Shell>
            }
          />
          <Route
            path="/login"
            element={
              <Shell>
                <Login />
              </Shell>
            }
          />
          <Route
            path="/register"
            element={
              <Shell>
                <Register />
              </Shell>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <Shell>
                <ForgotPassword />
              </Shell>
            }
          />
          <Route
            path="/reset-password"
            element={
              <Shell>
                <ResetPassword />
              </Shell>
            }
          />
          <Route
            path="/dashboard"
            element={
              <Shell>
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              </Shell>
            }
          />
          <Route
            path="/applicants"
            element={
              <Shell>
                <ProtectedRoute>
                  <Applicants />
                </ProtectedRoute>
              </Shell>
            }
          />
          <Route
            path="/compare"
            element={
              <Shell>
                <ProtectedRoute>
                  <Compare />
                </ProtectedRoute>
              </Shell>
            }
          />
          <Route
            path="/jobs/new"
            element={
              <Shell>
                <ProtectedRoute>
                  <CreateJob />
                </ProtectedRoute>
              </Shell>
            }
          />
          <Route
            path="/jobs/:jobId"
            element={
              <Shell>
                <ProtectedRoute>
                  <JobDetail />
                </ProtectedRoute>
              </Shell>
            }
          />
          <Route
            path="/jobs/:jobId/candidates/:interviewId"
            element={
              <Shell>
                <ProtectedRoute>
                  <Review />
                </ProtectedRoute>
              </Shell>
            }
          />
          <Route
            path="/billing"
            element={
              <Shell>
                <ProtectedRoute>
                  <Billing />
                </ProtectedRoute>
              </Shell>
            }
          />
          <Route
            path="/billing/success"
            element={
              <Shell>
                <ProtectedRoute>
                  <BillingSuccess />
                </ProtectedRoute>
              </Shell>
            }
          />
          <Route
            path="/admin"
            element={
              <Shell>
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              </Shell>
            }
          />
          <Route
            path="/settings"
            element={
              <Shell>
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              </Shell>
            }
          />
          <Route
            path="/learn"
            element={
              <Shell>
                <ProtectedRoute>
                  <Learn />
                </ProtectedRoute>
              </Shell>
            }
          />
          <Route
            path="/brief/:token"
            element={
              <Shell hideNav>
                <BriefShare />
              </Shell>
            }
          />
          <Route
            path="/team"
            element={
              <Shell>
                <ProtectedRoute>
                  <Team />
                </ProtectedRoute>
              </Shell>
            }
          />
          <Route
            path="/accept-invite"
            element={
              <Shell>
                <AcceptInvite />
              </Shell>
            }
          />
          <Route
            path="/c/:employerId"
            element={
              <Shell hideNav>
                <PublicCareers />
              </Shell>
            }
          />
          <Route
            path="/c/:employerId/j/:jobId"
            element={
              <Shell hideNav>
                <PublicApply />
              </Shell>
            }
          />
          <Route
            path="/application-status"
            element={
              <Shell hideNav>
                <ApplicationStatus />
              </Shell>
            }
          />
          <Route
            path="/c/:employerId/status"
            element={
              <Shell hideNav>
                <ApplicationStatus />
              </Shell>
            }
          />
          <Route
            path="/upload-resume/:token"
            element={
              <Shell hideNav>
                <UploadResume />
              </Shell>
            }
          />
          <Route
            path="/digest-action/:token"
            element={
              <Shell hideNav>
                <DigestAction />
              </Shell>
            }
          />
          <Route
            path="/withdraw/:token"
            element={
              <Shell hideNav>
                <WithdrawApplication />
              </Shell>
            }
          />
          <Route
            path="/bgcheck-data/:token"
            element={
              <Shell hideNav>
                <BgCheckDataForm />
              </Shell>
            }
          />
          <Route
            path="/:slug(cookies|privacy|terms|disclaimer)"
            element={
              <Shell>
                <PolicyPage />
              </Shell>
            }
          />
          <Route
            path="/cookies"
            element={<Shell><PolicyPage /></Shell>}
          />
          <Route
            path="/privacy"
            element={<Shell><PolicyPage /></Shell>}
          />
          <Route
            path="/terms"
            element={<Shell><PolicyPage /></Shell>}
          />
          <Route
            path="/disclaimer"
            element={<Shell><PolicyPage /></Shell>}
          />
          {/* Public candidate flow */}
          <Route
            path="/interview/:jobId"
            element={
              <Shell hideNav>
                <CandidateStart />
              </Shell>
            }
          />
          <Route
            path="/interview/session/:interviewId"
            element={
              <Shell hideNav>
                <InterviewRoom />
              </Shell>
            }
          />
          <Route
            path="/interview/session/:interviewId/complete"
            element={
              <Shell hideNav>
                <InterviewComplete />
              </Shell>
            }
          />
          {/* Live (camera-to-camera) */}
          <Route
            path="/jobs/:jobId/live/:roomId"
            element={
              <Shell hideNav>
                <ProtectedRoute>
                  <LiveHost />
                </ProtectedRoute>
              </Shell>
            }
          />
          <Route
            path="/live/:roomId"
            element={
              <Shell hideNav>
                <LiveCandidate />
              </Shell>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster richColors closeButton position="top-right" />
        <InstallPrompt />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
